// Calculs de temps dans les horaires d'ouverture de l'atelier (heure locale du navigateur).
import type { WorkingHours } from "../data/types.ts";

const MIN = 60_000;

function hm(s: string): [number, number] {
  const [h, m] = s.split(":").map(Number);
  return [h || 0, m || 0];
}

function isoDay(d: Date): number {
  const n = d.getDay();
  return n === 0 ? 7 : n;
}

/** Plages travaillées d'un jour donné, triées. Vide si le jour est chômé. */
export function dayRanges(day: Date, hours: WorkingHours): [Date, Date][] {
  if (!hours.days.includes(isoDay(day))) return [];
  return hours.ranges
    .map((r) => {
      const s = new Date(day); const [sh, sm] = hm(r.start); s.setHours(sh, sm, 0, 0);
      const e = new Date(day); const [eh, em] = hm(r.end); e.setHours(eh, em, 0, 0);
      return [s, e] as [Date, Date];
    })
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0].getTime() - b[0].getTime());
}

/** Premier instant travaillé à partir de t (t lui-même s'il est dans une plage). */
export function nextWorkingInstant(t: Date, hours: WorkingHours): Date {
  if (!hours.days.length || !hours.ranges.length) return new Date(t);
  const day = new Date(t); day.setHours(0, 0, 0, 0);
  for (let i = 0; i < 370; i++) {
    for (const [s, e] of dayRanges(day, hours)) {
      if (t < e) return t < s ? s : new Date(t);
    }
    day.setDate(day.getDate() + 1);
  }
  return new Date(t);
}

/** Ajoute des minutes de travail à t, en sautant les pauses, nuits et jours chômés. */
export function addWorkingMinutes(t: Date, minutes: number, hours: WorkingHours): Date {
  let cur = nextWorkingInstant(t, hours);
  let left = Math.max(0, minutes) * MIN;
  if (!hours.days.length || !hours.ranges.length) return new Date(cur.getTime() + left);
  for (let guard = 0; guard < 2000; guard++) {
    const day = new Date(cur); day.setHours(0, 0, 0, 0);
    const range = dayRanges(day, hours).find(([s, e]) => cur >= s && cur < e);
    if (!range) { cur = nextWorkingInstant(cur, hours); continue; }
    const room = range[1].getTime() - cur.getTime();
    if (left <= room) return new Date(cur.getTime() + left);
    left -= room;
    cur = nextWorkingInstant(range[1], hours);
  }
  return cur;
}

/** Minutes travaillées entre a et b (0 si b <= a). */
export function workingMinutesBetween(a: Date, b: Date, hours: WorkingHours): number {
  if (b <= a) return 0;
  let total = 0;
  const day = new Date(a); day.setHours(0, 0, 0, 0);
  for (let i = 0; i < 400 && day <= b; i++) {
    for (const [s, e] of dayRanges(day, hours)) {
      const from = Math.max(s.getTime(), a.getTime());
      const to = Math.min(e.getTime(), b.getTime());
      if (to > from) total += (to - from) / MIN;
    }
    day.setDate(day.getDate() + 1);
  }
  return Math.round(total);
}

export function startOfDay(d: Date): Date {
  const x = new Date(d); x.setHours(0, 0, 0, 0); return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d); x.setDate(x.getDate() + n); return x;
}

/** Lundi de la semaine de d. */
export function startOfWeek(d: Date): Date {
  const x = startOfDay(d); x.setDate(x.getDate() - (isoDay(x) - 1)); return x;
}

// ---------------------------------------------------------------------------
// Affichage
// ---------------------------------------------------------------------------
export function fmtDuration(min: number | null | undefined): string {
  if (min == null || !isFinite(min)) return "—";
  const m = Math.round(min);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), r = m % 60;
  return r ? `${h} h ${String(r).padStart(2, "0")}` : `${h} h`;
}

export function fmtTime(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export function fmtDate(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
}

export function fmtDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const today = startOfDay(new Date());
  const day = startOfDay(d);
  const diff = Math.round((day.getTime() - today.getTime()) / 86_400_000);
  const label = diff === 0 ? "aujourd'hui" : diff === 1 ? "demain" : diff === -1 ? "hier" : fmtDate(d);
  return `${label} ${fmtTime(d)}`;
}

/** Valeur pour <input type="datetime-local">. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

export function minutesSince(iso: string | null | undefined, now = new Date()): number {
  if (!iso) return 0;
  return Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / MIN));
}
